import { ELASTIC_MAX_PAGES, IS_PREVIEW } from '@/utils/constants';
import { isArray, isEmpty } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { DefaultElasticPagination } from '../ElasticComponent/ElasticComponentContext';
import {
  mapFacetsElasticToReact,
  mapFacetsToElastic,
  mapResultFieldsToElastic,
  newMapFacetsElasticToReact,
  parseKeywords,
  parsePagination,
  parseSortByDefault,
  removeEmpty,
  setFacetNumbers,
  wrapQuery,
} from './elasticSearch/useElasticSearch.utils';
import { UseElasticSearchProps } from './useElasticSearch.interfaces';
import { searchEngineService, useSearchEngine } from './useElasticSearchServices';

// TODO: This languages list should come from the BR value list.
const allLangs = ['en', 'fr', 'de', 'nl', 'pt', 'es', 'zh', 'ko', 'ja', 'it'];

export const useElasticSearch = ({
  engine = 'site-search',
  authEngine = engine,
  searchParams,
  facets = [],
  searchKey,
  fields,
  filterType = 'all',
  hideFilters = [],
  useDynamicFacets = true,
  searchFields,
  initBodyFilters = [],
  ignoredResultFields = [],
  onlyPublished = true,
  parseSortBy = parseSortByDefault,
  filtersFallback = {},
  language,
  labels = {},
  excludedFilters = [],
  needsUpdate = true,
  setNeedsUpdateState,
  clearResultOnFail = false,
}: UseElasticSearchProps) => {
  const filtersFallbackMap = useMemo(
    () =>
      Object.keys(filtersFallback).reduce((map, key) => {
        map.set(key, filtersFallback[key]);
        return map;
      }, new Map<string, string[]>()),
    [filtersFallback],
  );
  const [dynamicFacets, setDynamicFacets] = useState(useDynamicFacets);
  const [response, setResponse] = useState<any>({
    facets: [],
    pagination: DefaultElasticPagination,
  });
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [readyToQuery] = React.useState(true);
  const [facetsFilters, setFacetsFilters] = useState<any[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<any>({});
  const [facetsValues, setFacetsValues] = useState<any>({});
  const [isCached, setIsCached] = useState<boolean>(false);
  // Remove false values from searchParams.filters
  const filters = useMemo<any>(() => {
    return Object.keys(searchParams.filters).reduce((prev, key) => {
      if (searchParams.filters[key] === 'false') {
        return prev;
      }

      if (isArray(searchParams.filters[key])) {
        return {
          ...prev,
          [key]: searchParams.filters[key].filter((value: any) => value !== 'false'),
        };
      }

      return { ...prev, [key]: searchParams.filters[key] };
    }, {});
  }, [searchParams.filters]);

  const allFilters = useMemo(
    () => ({
      ...filters,
      ...Object.keys(filtersFallback).reduce(
        (prev: any, curr: string) => ({
          ...prev,
          [curr]: filters[curr] || filtersFallback[curr],
        }),
        {},
      ),
    }),
    [filters, filtersFallback],
  );

  const query = useMemo(
    () => ({
      q: wrapQuery(searchParams.q),
      page: searchParams.page,
      sortBy: parseSortBy(searchParams.sortBy, searchParams.sortType),
      size: searchParams.size ? +searchParams.size : 10,
      keywords: parseKeywords(searchParams.keywords),
      filters: removeEmpty({
        filters: allFilters,
        type: filterType,
        onlyPublished,
        filtersFallbackMap,
      }),
    }),
    [
      allFilters,
      filterType,
      onlyPublished,
      parseSortBy,
      filtersFallbackMap,
      searchParams.keywords,
      searchParams.page,
      searchParams.q,
      searchParams.size,
      searchParams.sortBy,
      searchParams.sortType,
    ],
  );

  const currentPage = parseInt(query.page as string, 10);

  const body = useMemo(() => {
    const nextBody: any = {
      query: query?.q + query.keywords,
      filters: query?.filters,
      sort: query?.sortBy,
      page: {
        current: currentPage < ELASTIC_MAX_PAGES ? currentPage : ELASTIC_MAX_PAGES,
        size: query.size,
      },
    };

    const initBody: any = { query: '' };

    if (facets.length > 0) {
      nextBody.facets = mapFacetsToElastic(facets);
      initBody.facets = mapFacetsToElastic(facets);
    }

    if (fields) {
      if (fields?.custom) {
        const { custom, ...otherFields } = fields;
        nextBody.result_fields = otherFields;
      } else {
        const params = !ignoredResultFields.length
          ? fields
          : Object.keys(fields).reduce((prev, curr) => {
              if (ignoredResultFields.find((field) => field === curr)) {
                return { ...prev };
              }

              return { ...prev, [curr]: fields[curr] };
            }, {});

        nextBody.result_fields = mapResultFieldsToElastic(params);
      }
    }

    if (searchFields) {
      nextBody.search_fields = searchFields;
    }

    const bodyFiltersFiltered = nextBody.filters[filterType]
      .map((filter: any) => {
        return initBodyFilters.reduce((prev, curr) => {
          const filterValue = filtersFallbackMap.has(curr)
            ? filtersFallbackMap.get(curr)
            : filters[curr];

          return filter[curr]
            ? {
                ...prev,
                [curr]: filterValue,
              }
            : { ...prev };
        }, {});
      })
      .filter((filter: any) => !isEmpty(filter));

    if (!isEmpty(initBodyFilters)) {
      initBody.filters = {
        [filterType]: onlyPublished
          ? [...bodyFiltersFiltered, { published: 'true' }]
          : [...bodyFiltersFiltered],
      };
    } else if (onlyPublished) {
      initBody.filters = { [filterType]: [{ published: 'true' }] };
    }

    if (language && !facets.includes('language')) {
      /* Elastic doesn't have logic for empty fields, 
         so we have to exclude other languages in case the `language` value is missing */
      const none = allLangs.filter((l) => l !== language).map((l) => ({ language: l }));

      initBody.filters = {
        ...initBody.filters,
        all: [...initBody.filters?.all, { none: [...none, ...excludedFilters] }],
      };
      nextBody.filters = {
        ...nextBody.filters,
        all: [...nextBody.filters?.all, { none: [...none, ...excludedFilters] }],
      };
    }
    if (!language && excludedFilters?.length) {
      initBody.filters = {
        ...initBody.filters,
        all: [...initBody.filters?.all, { none: [...excludedFilters] }],
      };
      nextBody.filters = {
        ...nextBody.filters,
        all: [...nextBody.filters?.all, { none: [...excludedFilters] }],
      };
    }

    return {
      initBody,
      nextBody,
    };
  }, [
    query?.q,
    query.keywords,
    query?.filters,
    query?.sortBy,
    query.size,
    currentPage,
    facets,
    fields,
    searchFields,
    filterType,
    initBodyFilters,
    onlyPublished,
    language,
    excludedFilters,
    ignoredResultFields,
    filtersFallbackMap,
    filters,
  ]);

  const { retrieveSearchEngineData } = useSearchEngine({ engine, authEngine });

  const reloadFacets = useCallback(() => {
    setDynamicFacets(true);
    setTimeout(() => {
      setDynamicFacets(false);
    }, 500);
  }, []);

  useEffect(() => {
    const fetchFacets = async () => {
      try {
        const searchEngineData = await retrieveSearchEngineData({
          data: body.initBody,
          searchToken: searchKey,
        });
        setFacetsFilters(newMapFacetsElasticToReact(facets, searchEngineData?.facets, labels));
      } catch (err) {
        setError(err as any);
      }
    };

    if (searchKey && dynamicFacets && !IS_PREVIEW) {
      fetchFacets();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicFacets, engine]);

  useDeepCompareEffect(() => {
    let isMounted = true;

    if (readyToQuery && needsUpdate) {
      setSelectedFilters(searchParams?.filters);
      const fetchData = async () => {
        try {
          setIsLoading(true);

          const searchEngineData = await retrieveSearchEngineData({
            data: body.nextBody,
            searchToken: searchKey,
          });

          if (isMounted) {
            setFacetsValues(mapFacetsElasticToReact(facets, searchEngineData.facets));
            setResponse({
              ...searchEngineData,
              pagination: parsePagination(searchEngineData.meta.page),
            });
            setIsLoading(false);

            if (setNeedsUpdateState) {
              setNeedsUpdateState(false);
            }
          }
        } catch (err) {
          if (isMounted) {
            setError(err as any);

            if (clearResultOnFail) {
              setResponse({});
            }

            if (setNeedsUpdateState) {
              setNeedsUpdateState(false);
            }
          }
        }
      };

      if (searchKey && !IS_PREVIEW) {
        fetchData();
      }
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchKey,
    readyToQuery,
    searchParams?.filters,
    needsUpdate,
    body.nextBody,
    engine,
    clearResultOnFail,
  ]);

  const processedFacets = setFacetNumbers(
    facetsFilters,
    facetsValues,
    selectedFilters,
    hideFilters,
    labels,
  );

  return {
    response,
    error,
    isLoading,
    facets: processedFacets,
    isCached,
    reloadFacets,
  };
};

export const FetchAllResults = async ({
  engine = 'dev-sdl',
  searchKey,
  searchParams = {},
  search_fields,
}: any): Promise<any> => {
  const PAGINATION_SIZE = 1000;
  const commonConfig = {
    headers: {
      Authorization: `Bearer ${searchKey}`,
    },
  };

  try {
    const FIRST: any = await searchEngineService({
      engine,
      data: {
        ...searchParams,
        query: wrapQuery(searchParams?.query),
        page: {
          size: PAGINATION_SIZE,
        },
        search_fields,
      },
      urlPath: '/search',
      isAuthenticated: false,
      config: commonConfig,
    });

    const TOTAL = FIRST.data.meta.page.total_results;
    const PAGES = Math.ceil(TOTAL / PAGINATION_SIZE);
    const QUERIES = [];

    for (let i = 1; i < PAGES; i += 1) {
      const NEXT: any = searchEngineService({
        engine,
        data: {
          ...searchParams,
          page: {
            current: i + 1,
            size: PAGINATION_SIZE,
          },
          search_fields,
        },
        urlPath: '/search',
        isAuthenticated: false,
        config: commonConfig,
      });
      QUERIES.push(NEXT);
    }

    const RESULTS = await Promise.all(QUERIES);

    const ALL_RESULTS = RESULTS.reduce(
      (state, response) => [...(state as any[]), ...(response as any).data.results],
      [...FIRST.data.results],
    );

    return { results: ALL_RESULTS };
  } catch (err) {
    return err;
  }
};

export const trackSearchClick = (
  token: string,
  query: string | string[],
  engine = 'dev-resources',
  document_id: string,
  request_id?: string,
  tags?: string[],
) => {
  if (IS_PREVIEW) return;

  return searchEngineService({
    urlPath: '/click',
    engine,
    data: {
      query,
      document_id,
      ...(request_id && { request_id }),
      ...(tags && { tags }),
    },
    isAuthenticated: false,
    config: {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  });
};
